import { ChangeEvent } from 'react'
import {
  setEnabledPBIAutoPay,
  setIsUpdatingCLAutopayEnabled,
  useAppDispatch,
  useCohort,
  useIsAfterpayFinanceFeeEligible,
  useIsConsumerLendingAutopayEnabled,
  useIsConsumerLendingOrder,
  useIsEnabledPBIAutoPay,
} from '../../state'
import { TrackingEvent } from '../../model'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import { Cohort } from '@afterpaytouch/portal-api'

type AutoPayStatusChangeHandler = (evOrValue: ChangeEvent<HTMLInputElement> | boolean) => void
export function useGetAutoPayStatusChangeHandler(type: 'event'): (ev: ChangeEvent<HTMLInputElement>) => void
export function useGetAutoPayStatusChangeHandler(type: 'value'): (value: boolean) => void

export function useGetAutoPayStatusChangeHandler(type: 'event' | 'value'): AutoPayStatusChangeHandler {
  const dispatch = useAppDispatch()
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const updateAutoPay = isConsumerLendingOrder ? setIsUpdatingCLAutopayEnabled : setEnabledPBIAutoPay

  if (type === 'event') {
    return (ev: ChangeEvent<HTMLInputElement>): void => {
      dispatch(updateAutoPay(ev.target.checked))
      const eventName = isConsumerLendingOrder ? TrackingEvent.TOGGLED_CL_AUTO_PAY_TOGGLE : TrackingEvent.TOGGLED_SUMMARY_PAGE_AUTOPAY_TOGGLE
      const autopayValue = ev.target.checked ? 'on' : 'off'
      logEvent(eventName, { autopay: autopayValue })
    }
  }

  return (value: boolean): void => {
    dispatch(updateAutoPay(value))
    const eventName = isConsumerLendingOrder
      ? value
        ? TrackingEvent.PRESSED_AUTOPAY_ON_PAGE_ENABLE_BUTTON
        : TrackingEvent.PRESSED_AUTOPAY_OFF_PAGE_DISABLE_BUTTON
      : TrackingEvent.PRESSED_SUMMARY_PAGE_CHANGE_AUTOPAY_BUTTON
    const eventValue = value ? 'enabled' : 'disabled'
    logEvent(eventName, { autopay: eventValue })
  }
}

export const useGetIsAutoPayAgreed = (): boolean => {
  const isConsumerLendingOrder = useIsConsumerLendingOrder()
  const isEnabledCLAutoPay = useIsConsumerLendingAutopayEnabled()
  const isEnabledPBIAutoPay = useIsEnabledPBIAutoPay()
  return isConsumerLendingOrder ? isEnabledCLAutoPay : isEnabledPBIAutoPay
}

export const useIsAutoPayButtonEligibleSession = (): boolean => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly
  const isPayMonthlyOrder = useIsConsumerLendingOrder()
  const isAFF = useIsAfterpayFinanceFeeEligible()

  return isArcadeDesignLanguage && (isCashOnlyCohort || isPayMonthlyOrder || isAFF)
}
